import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CrmManagerEventInterestsDto, type CrmPatchManagerEventInterestsBody } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Label } from '@upme/ui/components/label';
import { useEffect, useState } from 'react';
import { api } from '../../../app/api-client';
import { formatDateTime } from '../../users/lib/format';
import {
  crmContactContextKey,
  crmManagerEventInterestsKey,
  crmManagerEventInterestsOptions,
} from '../queries';

type Props = {
  contactUserId: string;
};

function tagsToField(tags: string[]): string {
  return tags.join(', ');
}

function fieldToTags(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatAuthor(author: CrmManagerEventInterestsDto['updatedBy']): string {
  if (!author) return '—';
  if (author.username) return `@${author.username}`;
  if (author.firstName) return author.firstName;
  return '—';
}

export function CrmManagerEventInterestsSection({ contactUserId }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery(crmManagerEventInterestsOptions(contactUserId));

  const [text, setText] = useState('');
  const [tagsField, setTagsField] = useState('');

  useEffect(() => {
    if (!data) return;
    setText(data.text ?? '');
    setTagsField(tagsToField(data.tags));
  }, [data]);

  const save = useMutation({
    mutationFn: (body: CrmPatchManagerEventInterestsBody) =>
      api
        .patch(`admin/crm/contacts/${contactUserId}/manager-event-interests`, { json: body })
        .json()
        .then((json) => CrmManagerEventInterestsDto.parse(json)),
    onSuccess: (next) => {
      queryClient.setQueryData(crmManagerEventInterestsKey(contactUserId), next);
      void queryClient.invalidateQueries({ queryKey: crmContactContextKey(contactUserId) });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'board'] });
    },
  });

  const dirty = data && (text !== (data.text ?? '') || tagsField !== tagsToField(data.tags));

  function handleSave() {
    const trimmed = text.trim();
    save.mutate({
      text: trimmed.length > 0 ? trimmed : null,
      tags: fieldToTags(tagsField),
    });
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Загрузка интересов…</p>;
  }
  if (isError || !data) {
    return <p className="text-sm text-muted-foreground">Не удалось загрузить интересы менеджера.</p>;
  }

  return (
    <section className="space-y-3 border-t border-border/60 pt-4">
      <div className="space-y-1">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Интересы к мероприятиям (CRM)
        </h3>
        <p className="text-xs text-muted-foreground">
          Внутренние заметки менеджера. Не видны пользователю и не совпадают с блоком «Мероприятия» в профиле.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`crm-interests-text-${contactUserId}`}>Заметка</Label>
        <textarea
          id={`crm-interests-text-${contactUserId}`}
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={4000}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          placeholder="Что человеку интересно, ограничения, контекст для приглашений…"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`crm-interests-tags-${contactUserId}`}>Теги (через запятую, до 10)</Label>
        <textarea
          id={`crm-interests-tags-${contactUserId}`}
          rows={2}
          value={tagsField}
          onChange={(e) => setTagsField(e.target.value)}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          placeholder="нетворкинг, йога, бизнес-завтраки"
        />
        <p className="text-xs text-muted-foreground">тегов: {fieldToTags(tagsField).length}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" disabled={!dirty || save.isPending} onClick={handleSave}>
          {save.isPending ? 'Сохранение…' : 'Сохранить'}
        </Button>
        {save.isError ? <span className="text-xs text-destructive">Не удалось сохранить</span> : null}
        {data.updatedAt ? (
          <span className="text-xs text-muted-foreground">
            Обновлено {formatDateTime(data.updatedAt)} · {formatAuthor(data.updatedBy)}
          </span>
        ) : null}
      </div>
    </section>
  );
}
